import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Zap, Check, Slack, Mail, X, Plus } from 'lucide-react';
import { elodge } from '@/lib/api';

export default function FleetAlertsCard() {
  const [cfg, setCfg] = React.useState(null);
  const [slack, setSlack] = React.useState('');
  const [recipients, setRecipients] = React.useState([]);
  const [threshold, setThreshold] = React.useState(30);
  const [newRecipient, setNewRecipient] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const [saved, setSaved] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const r = await elodge.alerts.get();
      setCfg(r);
      setSlack('');  // never populate secret
      setRecipients(r.alert_recipients || []);
      setThreshold(r.stuck_threshold_min || 30);
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Cannot load alert config' });
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const patch = { alert_recipients: recipients, stuck_threshold_min: Number(threshold) };
      if (slack) patch.slack_webhook_url = slack;
      await elodge.alerts.save(patch);
      setSaved(true); setTimeout(() => setSaved(false), 3000);
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Save failed' });
    } finally { setBusy(false); }
  };

  const test = async () => {
    setTesting(true); setMsg(null);
    try {
      const r = await elodge.alerts.test();
      if (r.error) {
        setMsg({ ok: false, text: r.error });
      } else {
        setMsg({ ok: true, text: `Test dispatched — Slack: ${r.slack_ok ? '✓' : '—'} · Emails delivered: ${r.email_ok}/${r.recipients_configured}` });
      }
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Test failed' });
    } finally { setTesting(false); setTimeout(() => setMsg(null), 6000); }
  };

  const addRecipient = () => {
    const v = (newRecipient || '').trim().toLowerCase();
    if (!v || !v.includes('@')) return;
    if (recipients.includes(v)) return;
    if (recipients.length >= 10) { setMsg({ ok: false, text: 'Max 10 recipients' }); return; }
    setRecipients((r) => [...r, v]); setNewRecipient('');
  };
  const removeRecipient = (r) => setRecipients((rs) => rs.filter((x) => x !== r));

  if (!cfg) return null;

  return (
    <Card className="border-slate-200" data-testid="fleet-alerts-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-amber-500" />
          <div className="text-sm font-semibold text-[#1A3A5C]">eLodge fleet alerts</div>
          {saved && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]"><Check className="mr-1 h-3 w-3"/> Saved</Badge>}
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Ping your ops channel the moment a lodgement job stalls in <b>awaiting_human</b> — so nobody has to babysit the fleet dashboard for CAPTCHA / MFA prompts.
        </p>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <Label className="flex items-center gap-1 text-xs"><Slack className="h-3 w-3" /> Slack incoming webhook</Label>
            <Input value={slack} onChange={(e) => setSlack(e.target.value)}
                   data-testid="fleet-alerts-slack"
                   placeholder={cfg.has_slack_webhook ? cfg.slack_webhook_url_masked : 'https://hooks.slack.com/services/T00.../B00.../XXX'}
                   className="mt-1 h-9 font-mono text-[11px]" />
            <div className="mt-1 text-[10px] text-slate-500">
              {cfg.has_slack_webhook ? 'A webhook is already saved (leave blank to keep). Type a new URL to replace.' : 'Optional. Get one from Slack Apps → Incoming Webhooks.'}
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-1 text-xs"><Mail className="h-3 w-3" /> Stuck-job threshold (min)</Label>
            <Input type="number" min={5} max={360} value={threshold}
                   onChange={(e) => setThreshold(e.target.value)}
                   data-testid="fleet-alerts-threshold"
                   className="mt-1 h-9 w-32 text-xs" />
            <div className="mt-1 text-[10px] text-slate-500">Between 5 and 360 minutes. Alerts fire once per job.</div>
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-xs">Email recipients ({recipients.length}/10)</Label>
          <div className="mt-1 flex gap-1.5">
            <Input value={newRecipient} onChange={(e) => setNewRecipient(e.target.value)}
                   onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRecipient())}
                   data-testid="fleet-alerts-new-recipient"
                   placeholder="ops@yourfirm.com.au"
                   className="h-8 flex-1 text-xs" />
            <Button size="sm" variant="outline" onClick={addRecipient} data-testid="fleet-alerts-add-recipient"
                    className="h-8 gap-1 text-xs">
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
          {recipients.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1" data-testid="fleet-alerts-recipients-list">
              {recipients.map((r) => (
                <span key={r} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] text-slate-700">
                  {r}
                  <button onClick={() => removeRecipient(r)} className="text-slate-400 hover:text-red-500"
                          data-testid={`fleet-alerts-remove-${r}`}>
                    <X className="h-2.5 w-2.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {msg && (
          <div data-testid="fleet-alerts-msg"
               className={`mt-3 rounded-md p-2 text-xs ${msg.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            {msg.text}
          </div>
        )}

        <div className="mt-4 flex items-center gap-2">
          <Button data-testid="fleet-alerts-save" onClick={save} disabled={busy}
                  className="h-9 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save alert config'}
          </Button>
          <Button data-testid="fleet-alerts-test" onClick={test} disabled={testing || (!cfg.has_slack_webhook && recipients.length === 0 && !slack)}
                  variant="outline" className="h-9 gap-1 text-xs">
            {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />} Send test alert
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
